import React, { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
  ButtonColorTypes,
} from "../../shared-components/Button";
import { XIcon } from "../../shared-components/icons";
import InputField from "../../shared-components/InputField";
import { classNames, uniqueUUID } from "../../utils";
import { PersonFormStateError } from "../PersonFormErrorState";
import { profileUrlErrorMessage } from "../../utils/personFieldLimits";

export interface SocialDefinitions {
  key: string;
  url: string;
}

type Props = {
  onChange?: (emails: SocialDefinitions[]) => void;
  error?: PersonFormStateError;
  prefill?: SocialDefinitions[];
  alternateColor?: boolean;
};

function buildEmptyDefinition(): SocialDefinitions {
  return { url: "", key: uniqueUUID() };
}

const PersonFormSocial = ({
  onChange,
  error,
  prefill,
  alternateColor,
}: Props) => {
  const [fieldsList, setFields] = useState<SocialDefinitions[]>([]);

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (idx: number, field: "url", value: string) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: SocialDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (idx: number) => {
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length >= 1) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <div className="flex">
      {fieldsList.length > 0 && (
        <div className="flex-1 mr-4">
          {fieldsList.map((fields, idx) => {
            const fieldError =
              error?.profiles?.[idx]?.message ||
              profileUrlErrorMessage(fieldsList[idx].url, {
                allowEmpty: true,
              });
            return (
              <div
                key={fields.key}
                className={classNames(
                  "flex flex-col mr-4 w-full",
                  idx > 0 ? "mt-4" : ""
                )}
              >
                <div className="flex flex-wrap items-center w-full">
                  <InputField
                    handleChange={(e) => {
                      updateField(idx, "url", e.target.value);
                    }}
                    name={`social-${idx}`}
                    placeholder="http://twitter.com/username"
                    value={fieldsList[idx].url}
                    label={"Profile Url"}
                    classes="flex-1 mr-4"
                    className={classNames(
                      `${fieldError ? "border-rose-600" : ""}`,
                      "w-full"
                    )}
                  />
                  {fieldsList.length >= 0 && (
                    <span className="pl-2 h-0">
                      <div
                        className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                        onClick={(e) => handleRemove(idx)}
                      >
                        <XIcon />
                      </div>
                    </span>
                  )}
                </div>
                {fieldError && (
                  <div className="text-red-500 text-sm mt-2">{fieldError}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="self-end">
        <Button
          type={ButtonTypes.add}
          text="Add Profile"
          colorType={
            alternateColor
              ? ButtonColorTypes.alternate
              : ButtonColorTypes.action
          }
          className="w-44"
          onClick={handleAdd}
        />
      </div>
    </div>
  );
};

export default PersonFormSocial;
